import { useEffect, useRef, useState } from "react"
import {
  HALO_PAD,
  ICON_HEIGHT,
  MIX_TAU,
  buildHaloGeometry,
  drawHaloFrame,
  iconAnchors,
  iconRadius,
  stepMix,
} from "./haloGeometry"

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export default function ChainHalo({ lit, src, height = ICON_HEIGHT }) {
  const canvasRef = useRef(null)
  const sceneRef = useRef(null)
  const litRef = useRef(lit)
  const [reduced, setReduced] = useState(prefersReducedMotion)
  const [ready, setReady] = useState(false)
  litRef.current = lit

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = () => setReduced(query.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  useEffect(() => {
    if (reduced) return undefined
    const canvas = canvasRef.current
    if (!canvas) return undefined
    let cancelled = false
    const image = new Image()
    image.onload = () => {
      if (cancelled || !image.naturalWidth || !image.naturalHeight) return
      const iconHeight = height
      const iconWidth = (image.naturalWidth / image.naturalHeight) * iconHeight
      const sampleWidth = Math.max(1, Math.round(iconWidth))
      const sampleHeight = Math.max(1, Math.round(iconHeight))
      const cssWidth = sampleWidth + HALO_PAD * 2
      const cssHeight = sampleHeight + HALO_PAD * 2
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const pixelWidth = Math.round(cssWidth * dpr)
      const pixelHeight = Math.round(cssHeight * dpr)
      canvas.style.width = `${cssWidth}px`
      canvas.style.height = `${cssHeight}px`
      canvas.width = pixelWidth
      canvas.height = pixelHeight
      const sample = document.createElement("canvas")
      sample.width = sampleWidth
      sample.height = sampleHeight
      const sampleCtx = sample.getContext("2d", { willReadFrequently: true })
      sampleCtx.drawImage(image, 0, 0, sampleWidth, sampleHeight)
      const imageData = sampleCtx.getImageData(0, 0, sampleWidth, sampleHeight)
      sceneRef.current = {
        ctx: canvas.getContext("2d"),
        cssWidth,
        cssHeight,
        dpr,
        loops: buildHaloGeometry(imageData.data, sampleWidth, sampleHeight, HALO_PAD),
        anchors: iconAnchors(HALO_PAD, HALO_PAD, sampleWidth, sampleHeight),
        radius: iconRadius(sampleWidth),
        mix: 0,
      }
      setReady(true)
    }
    image.src = src
    return () => {
      cancelled = true
      sceneRef.current = null
      setReady(false)
    }
  }, [height, reduced, src])

  useEffect(() => {
    if (reduced || !ready) return undefined
    let frame = 0
    let last = performance.now()
    const tick = (now) => {
      const scene = sceneRef.current
      if (!scene) return
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000))
      last = now
      const target = litRef.current ? 1 : 0
      scene.mix = stepMix(scene.mix, target, dt, MIX_TAU)
      drawHaloFrame(scene.ctx, {
        cssWidth: scene.cssWidth,
        cssHeight: scene.cssHeight,
        dpr: scene.dpr,
        loops: scene.loops,
        anchors: scene.anchors,
        radius: scene.radius,
        mix: scene.mix,
        time: now / 1000,
      })
      if (litRef.current || scene.mix > 0.004) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [lit, ready, reduced])

  if (reduced) return null

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute"
      style={{ left: -HALO_PAD, top: -HALO_PAD }}
      aria-hidden="true"
    />
  )
}
